/**
 * 궤도 이탈 감지기.
 *
 * 운석이 지구로부터 너무 멀리 — escape radius 를 넘어 — 날아가면 화면
 * 밖으로 사라진 것으로 간주하고 escape 이벤트를 발화한다. 이는 대기권
 * 경계에 머무르는 "대기권 돌파(breach)" 와는 다른 페널티: breach 는
 * 지구 표면 가까이 정착한 운석이 가하는 구조적 압력이고, escape 는
 * 운석을 잃어버린(통제 실패) 사건이다.
 *
 * 둘을 분리하기 위해 escape 는:
 *   - 정착 여부와 무관 (속도 무관)
 *   - 단발성 이벤트 — 메테오는 즉시 제거되어야 함
 *   - 별도 UI 배너 ("🌌 궤도 이탈!") + 별도 (작은) 페널티
 *
 * Pure logic — 실제 sim.removePlanet 호출은 caller 가 담당.
 */

export interface EscapeListener {
  (info: EscapeInfo): void;
}

export interface EscapeInfo {
  meteorId: number;
  tierIndex: number;
  /** 안커(지구 중심) 으로부터의 거리 — 페널티 스케일링용. */
  distance: number;
}

interface MeteorSnapshot {
  id: number;
  distance: number;
  tierIndex: number;
}

export interface EscapeOptions {
  /** 이 거리를 넘어가면 escape 으로 간주. */
  escapeRadius: number;
}

export interface EscapeDetector {
  /** 매 프레임 호출 — escape 한 운석 id 목록 반환 (caller 가 sim 에서 제거). */
  update(meteors: ReadonlyArray<MeteorSnapshot>): ReadonlyArray<number>;
  /** escape 이벤트 구독. listener 는 escape 시 한 번 호출됨. */
  subscribe(listener: EscapeListener): () => void;
  reset(): void;
}

export function createEscapeDetector(opts: EscapeOptions): EscapeDetector {
  if (opts.escapeRadius <= 0) {
    throw new Error("createEscapeDetector: escapeRadius must be positive");
  }
  const escapeRadius = opts.escapeRadius;
  const listeners = new Set<EscapeListener>();
  /** 한 번 escape 으로 처리된 id 는 잠시 무시 — 같은 프레임 중복 방지 */
  const seenIds = new Set<number>();

  const update = (meteors: ReadonlyArray<MeteorSnapshot>): ReadonlyArray<number> => {
    const escaped: number[] = [];
    const stillActive = new Set<number>();
    for (const m of meteors) {
      stillActive.add(m.id);
      if (seenIds.has(m.id)) continue;
      if (m.distance > escapeRadius) {
        escaped.push(m.id);
        seenIds.add(m.id);
        for (const l of listeners) {
          l({ meteorId: m.id, tierIndex: m.tierIndex, distance: m.distance });
        }
      }
    }
    // GC: drop ids no longer present in sim.
    for (const id of seenIds) {
      if (!stillActive.has(id)) seenIds.delete(id);
    }
    return escaped;
  };

  const subscribe = (l: EscapeListener): (() => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
  };

  const reset = (): void => {
    seenIds.clear();
  };

  return { update, subscribe, reset };
}

export const __testing = {};
